/* eslint-disable jsx-a11y/label-has-associated-control */
import React from 'react';
import type { RegisterOptions } from 'react-hook-form';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { FieldWithoutPermission } from '@/components/PermissionedField';
import { useAbility } from '@/hooks/ability';
import { cn } from '@/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

const formItemVariants = cva('flex w-auto flex-col font-medium dark:text-white', {
  variants: {
    hidden: {
      true: 'h-0 w-0 overflow-hidden',
      false: '',
    },
    variant: {
      default: 'gap-2',
      compact: 'gap-1',
    },
  },
  defaultVariants: {
    hidden: false,
    variant: 'default',
  },
});

const labelVariants = cva('flex w-full flex-col', {
  variants: {
    required: {
      true: '',
      false: '',
    },
    variant: {
      default: 'text-base',
      compact: 'text-sm',
    },
  },
  defaultVariants: {
    required: false,
    variant: 'default',
  },
});

export interface FormItemProps extends VariantProps<typeof formItemVariants> {
  children: React.ReactNode;
  name: string;
  description?: string | React.ReactNode;
  rules?: RegisterOptions;
  label?: string | React.ReactNode;
  readable?: string;
  hideLabel?: boolean;
  permission?: string | string[];
  needAllPermissions?: boolean;
  variant?: 'default' | 'compact';
  className?: string;
}

export const FormItem = ({
  children,
  name,
  label,
  rules,
  hidden,
  description,
  readable,
  className,
  hideLabel,
  labelClassName,
  permission,
  needAllPermissions,
  variant = 'default',
  ...props
}: {
  children: React.ReactNode;
  name: string;
  description?: string | React.ReactNode;
  rules?: RegisterOptions;
  label?: string | React.ReactNode;
  readable?: string;
  hideLabel?: boolean;
  labelClassName?: string;
  permission?: string | string[];
  needAllPermissions?: boolean;
  variant?: 'default' | 'compact';
} & React.HTMLAttributes<HTMLDivElement>) => {
  const { control, formState } = useFormContext();
  const value = useWatch({ name: name as any });

  const labelIsString = typeof label === 'string';
  const ability = useAbility();

  const hasPermission = Array.isArray(permission)
    ? permission.length
      ? needAllPermissions
        ? permission.every(p => ability.can(p, ''))
        : permission.some(p => ability.can(p, ''))
      : true
    : !permission || ability.can(permission, '');

  return (
    <div className={cn(formItemVariants({ hidden, variant }), className)} {...props}>
      {hasPermission ? (
        <Controller
          control={control}
          name={name as any}
          rules={
            {
              ...rules,
              required: rules?.required ? `O campo ${labelIsString ? label : readable} é obrigatório` : undefined,
            } as any
          }
          render={({ field, fieldState: { error } }) => {
            return (
              <>
                {!hideLabel && (
                  <label className={labelVariants({ variant })} title={labelIsString ? label : readable}>
                    <div className={cn("flex w-full gap-1 overflow-hidden overflow-ellipsis whitespace-nowrap", labelClassName)}>
                      <span className="max-w-fit overflow-hidden overflow-ellipsis">{label}</span>
                      <span className="text-red-600 shrink-0">{rules?.required && label ? ' *' : ''}</span>
                    </div>
                  </label>
                )}

                {React.Children.map(children, child => {
                  if (React.isValidElement(child)) {
                    const clonedData = {
                      control,
                      ...child.props,
                      ...field,
                      value: value !== undefined ? value : field.value,
                      onChange: child.props.onChange
                        ? (e: any) => {
                            child.props.onChange(e);
                            field.onChange(e);
                          }
                        : field.onChange,
                      onBlur: child.props.onBlur
                        ? (e: any) => {
                            child.props.onBlur?.(e);
                            field.onBlur?.();
                          }
                        : (field.onBlur ?? undefined),
                      name,
                      disabled: formState.disabled || child.props.disabled,
                      className: twMerge(child.props.className, error?.message ? 'border-red-600' : ''),
                    };

                    if ((child as any).ref) {
                      clonedData.ref = (child as any)?.ref;
                    }

                    return React.cloneElement(child, clonedData);
                  }
                  return child;
                })}

                {description && (
                  <div className={cn('text-neutral-500', variant === 'compact' ? 'text-xs' : 'text-sm')}>
                    {description}
                  </div>
                )}

                {error?.message && (
                  <span
                    className={cn(
                      'w-full whitespace-break-spaces text-red-600',
                      variant === 'compact' ? 'text-xs' : 'text-sm',
                    )}
                  >
                    {error?.message as string | undefined}
                  </span>
                )}
              </>
            );
          }}
        />
      ) : (
        <FieldWithoutPermission
          title={permission ? (Array.isArray(permission) ? permission.join(', ') : permission) : ''}
        />
      )}
    </div>
  );
};
